<template>
   <div>
      <LoadingPage v-if="isLoading"></LoadingPage>

      <div class="card">
         <div class="card-body m-2">
            <div class="row p-2 align-items-center">
               <div class="col-12 col-lg-3 mb-2 mb-lg-0">
                  <input v-model.trim="search" type="text" class="form-control form-control-sm"
                     placeholder="🔍 Search Sample Contract Templates..."
                     style="border-radius:8px; height: 40px; font-size: 14px; font-family: 'Roboto', sans-serif; font-weight: 400;"
                     @keyup="onSearch()" />
               </div>

                 <div v-if="$store.getters['auth/canEditContentLibrary']" class="col-12 col-lg-auto d-flex mb-2 mb-lg-0">
                    <button type="button" class="btn-primary-gradient px-4"
                       style="font-size: 14px; height: 40px; display: inline-flex; align-items: center; gap: 8px;" @click="openUploadSampleTermsModal()">
                       <i class="fas fa-cloud-upload-alt"></i> Upload Sample Terms
                    </button>
                 </div>

               <div class="col-12 col-lg d-flex flex-wrap justify-content-lg-end align-items-center">
                  <div class="d-flex align-items-center me-3 mb-2 mb-md-0">
                     <span class="p-2 filterTitle">Sort By:</span>
                     <select v-model="sortBy" class="form-select shadow-none filterOptions bordered-select">
                        <option value="name_asc">Name</option>
                        <option value="date_desc">Newest</option>
                        <option value="date_asc">Oldest</option>
                     </select>
                  </div>
                  <div class="d-flex align-items-center me-3 mb-2 mb-md-0">
                     <span class="p-2 filterTitle">Rows per page:</span>
                     <select v-model="pagination.pageSize" class="form-select form-select-sm shadow-none filterOptions bordered-select">
                        <option v-for="row in rowsPerPageList" :value="row.value" :key="row.value">{{ row.text }}
                        </option>
                     </select>
                  </div>

                  <div class="pagination">
                     <button @click="previousPage()" :disabled="pagination.page === 1" class="pagination-arrow">
                         <i class="fas fa-chevron-left"></i>
                     </button>
                     <span class="pagination-label">
                         <strong>{{ paginationLabel }}</strong> of {{ pagination.totalItems }}
                     </span>
                     <button @click="nextPage()" :disabled="pagination.page === pagination.totalPages" class="pagination-arrow">
                         <i class="fas fa-chevron-right"></i>
                     </button>
                  </div>
               </div>
            </div>
         </div>
      </div>

      <div v-if="templateList.length > 0" class="row row-cols-1 row-cols-md-3 row-cols-lg-4 row-cols-xl-5 mt-3">
         <div v-for="template in templateList" :key="template.id" class="col mb-4">
            <div class="card h-100 contract-card">
               <div class="card-body d-flex flex-column">
                  <h5 class="card-title">{{ template.name }}</h5>
                  <hr class="my-3">
                  <p class="card-text mb-1"><strong>Status:</strong> <span class="status-in-use">{{ template.status }}</span></p>
                  <p class="card-text mb-1"><strong>Contract Type:</strong> {{ template.contractType }}</p>
                  <p class="card-text mb-1"><strong>Pricing Plan:</strong> {{ template.pricingPlan }}</p>
                  <p class="card-text mb-1"><strong>Date Created:</strong> {{ template.dateCreated }}</p>
                  <p class="card-text mb-1"><strong>Version:</strong> {{ template.version }}</p>

                  <hr class="mt-auto">
                  <div class="action-button-container pt-2">
                     <button v-if="$store.getters['auth/canEditContentLibrary']" type="button" class="btn-svg-icon" title="Edit Template" @click="openEditModal(template)">
                        <img src="@/assets/img/icons/universal_edit.svg" alt="Edit" class="icon-svg" />
                     </button>
                     <button type="button" class="btn-svg-icon" title="View Template" @click="openPdfViewer(template)">
                         <img src="@/assets/img/icons/preview.svg" alt="Preview" class="icon-svg" />
                     </button>
                     <button v-if="$store.getters['auth/isGlobalSuperAdmin']" type="button" class="btn-svg-icon btn-svg-icon--delete" title="Delete Template" @click="deleteTemplate(template.id)">
                         <img src="@/assets/img/icons/universal_delete.svg" alt="Delete" class="icon-svg" />
                     </button>
                  </div>
               </div>
            </div>
         </div>
      </div>
      <div v-if="templateList.length < 1 && !isLoading" class="text-center p-5">
         <p>No records to display.</p>
      </div>

      <div v-if="isUploadModalOpen" class="modal-backdrop-custom">
         <div class="modal-content-custom">
            <button @click="isUploadModalOpen = false" class="close-button-custom">&times;</button>
            <h5 class="modal-title-custom">Upload Sample Terms</h5>
            <hr>
            <form @submit.prevent="saveSampleTerm" class="modal-form-custom">
               <div class="mb-3">
                  <label for="sampleTermFile" class="form-label">Select PDF File <span style="color: red;">*</span></label>
                   <input type="file" id="sampleTermFile" ref="sampleTermFileRef" class="form-control" required accept=".pdf">
               </div>
               <div class="mb-3">
                  <label for="sampleTermName" class="form-label">Contract Name <span style="color: red;">*</span></label>
                  <input type="text" id="sampleTermName" class="form-control" v-model.trim="newSampleTerm.name" required @input="checkDuplicateName()">
                  <small v-if="isNewNameInvalid" class="text-danger">A contract with this name already exists.</small>
               </div>
               <div class="mb-3">
                  <label for="sampleTermStatus" class="form-label">Status</label>
                  <input type="text" id="sampleTermStatus" class="form-control" value="In Use" disabled>
               </div>
               <div class="mb-3">
                  <label for="sampleTermContractType" class="form-label">Contract Type</label>
                  <input type="text" id="sampleTermContractType" class="form-control" value="Sample Terms" disabled>
               </div>
               <div class="mb-3">
                  <label for="sampleTermPricingType" class="form-label">Pricing Type <span style="color: red;">*</span></label>
                  <select id="sampleTermPricingType" class="form-select" v-model="newSampleTerm.pricingType" required>
                     <option disabled value="">- Select a Pricing Type -</option>
                     <option>Recurring</option>
                     <option>One-off</option>
                  </select>
               </div>
               <div class="mt-4 text-center">
                  <button type="submit" class="btn-primary-gradient add-button-custom" :disabled="isLoading || isNewNameInvalid">
                     {{ isLoading ? 'Uploading...' : 'Upload' }}
                  </button>
               </div>
            </form>
         </div>
      </div>

      <div v-if="isAddModalOpen" class="modal-backdrop-custom">
         <div class="modal-content-custom">
            <button @click="isAddModalOpen = false" class="close-button-custom">&times;</button>
            <h5 class="modal-title-custom">Add Contract Template</h5>
            <hr>
            <form @submit.prevent="saveNewTemplate" class="modal-form-custom">
               <div class="mb-3">
                  <label for="templateFile" class="form-label">Select File <span style="color: red;">*</span></label>
                   <input type="file" id="templateFile" class="form-control" required>
               </div>
               <div class="mb-3">
                  <label for="templateName" class="form-label">Contract Template Name <span style="color: red;">*</span></label>
                  <input type="text" id="templateName" class="form-control" v-model.trim="newTemplate.name" placeholder="e.g., Cyber Security Proposal" required>
               </div>
               <div class="mb-3">
                  <label for="templateType" class="form-label">Contract Template Type <span style="color: red;">*</span></label>
                  <select id="templateType" class="form-select" v-model="newTemplate.type" required>
                     <option disabled value="">- Select a Type -</option>
                     <option value="Sample - Standard">Sample - Standard</option>
                     <option value="Client Specific">Client Specific</option>
                     <option value="Internal">Internal</option>
                  </select>
               </div>
               <div class="mb-3">
                  <label for="templateNotes" class="form-label">Notes</label>
                  <textarea id="templateNotes" class="form-control" v-model.trim="newTemplate.notes" rows="3" placeholder="Add any relevant notes here..."></textarea>
               </div>
               <div class="mt-4 text-center">
                  <button type="submit" class="btn-primary-gradient add-button-custom">Add Template</button>
               </div>
            </form>
         </div>
      </div>

      <div v-if="isEditModalOpen" class="modal-backdrop-custom">
         <div class="modal-content-custom">
            <button @click="isEditModalOpen = false" class="close-button-custom">&times;</button>
            <h5 class="modal-title-custom">Edit Sample Terms</h5>
            <hr>
            <form v-if="editingTemplate" @submit.prevent="updateSampleContract" class="modal-form-custom">
               <div class="mb-3">
                  <label for="editTemplateName" class="form-label">Contract Name <span style="color: red;">*</span></label>
                  <input type="text" id="editTemplateName" class="form-control" v-model.trim="editingTemplate.name" required @input="checkEditDuplicateName()">
                  <small v-if="isEditNameInvalid" class="text-danger">Another contract with this name already exists.</small>
               </div>
                <div class="mb-3">
                  <label for="editTemplateStatus" class="form-label">Status <span style="color: red;">*</span></label>
                  <select id="editTemplateStatus" class="form-select" v-model="editingTemplate.status" required>
                     <option>In Use</option>
                     <option>Draft</option>
                  </select>
               </div>
                <div class="mb-3">
                  <label for="editTemplateContractType" class="form-label">Contract Type</label>
                  <input type="text" id="editTemplateContractType" class="form-control" v-model.trim="editingTemplate.contractType" disabled>
               </div>
               <div class="mb-3">
                  <label for="editTemplatePricingType" class="form-label">Pricing Type <span style="color: red;">*</span></label>
                  <select id="editTemplatePricingType" class="form-select" v-model="editingTemplate.pricingPlan" required>
                     <option>Recurring</option>
                     <option>One-off</option>
                  </select>
               </div>
               <div class="mb-3">
                  <label for="editTemplateFile" class="form-label">Upload New Version</label>
                   <input type="file" id="editTemplateFile" ref="editTemplateFileRef" class="form-control" accept=".pdf">
                   <small class="form-text text-muted">Leave blank to keep the current file (Version: {{ editingTemplate.version }}).</small>
               </div>
               <div class="mt-4 text-center">
                  <button type="submit" class="btn-primary-gradient add-button-custom" :disabled="isLoading || isEditNameInvalid">
                      {{ isLoading ? 'Updating...' : 'Update' }}
                  </button>
               </div>
            </form>
         </div>
      </div>

      <div v-if="isPdfViewerOpen" class="modal-backdrop-custom">
         <div class="modal-content-custom pdf-viewer-modal">
            <button @click="isPdfViewerOpen = false" class="close-button-custom">&times;</button>
            <h5 class="modal-title-custom">Contract Viewer</h5>
            <hr>
            <iframe :src="pdfViewerUrl" frameborder="0" width="100%" height="100%"></iframe>
         </div>
      </div>

      <ConfirmModal
         v-model="showConfirmModal"
         :title="confirmModalTitle"
         :message="confirmModalMessage"
         @confirm="onConfirmAction"
      />

   </div>
</template>

<script>
import { debounce } from '@/plugins/helper.js';
import moment from 'moment';
import LoadingPage from '@/components/Utilities/vertex-loading.vue';

export default {
   name: 'ContractTemplatesDashboard',
   components: {
      LoadingPage,
   },
   data() {
      return {
         isLoading: false,
         isAddModalOpen: false,
         isUploadModalOpen: false,
         isPdfViewerOpen: false,
         isEditModalOpen: false,
         pdfViewerUrl: "",
         search: "",
         allTemplates: [],
         templateList: [],
         newTemplate: {
            name: "",
            type: "",
            notes: "",
         },
         newSampleTerm: {
            name: "",
            file: null,
            pricingType: "",
         },
         editingTemplate: null,
         pagination: {
            pageSize: 5,
            page: 1,
            totalItems: 0,
            totalPages: 1,
         },
         rowsPerPageList: [{ text: "5", value: 5 }, { text: "10", value: 10 }, { text: "15", value: 15 }],
         sortBy: "name_asc",
         debounceFn: null,
         isNewNameInvalid: false,
         isEditNameInvalid: false,
         showConfirmModal: false,
         confirmModalTitle: '',
         confirmModalMessage: '',
         pendingDeleteId: null,
      };
   },
   created() {
      this.fetchContracts();
      this.debounceFn = debounce(() => this.processClientSideData(), 500);
   },
   computed: {
      paginationLabel() {
         const start = Math.min((this.pagination.page - 1) * this.pagination.pageSize + 1, this.pagination.totalItems);
         const end = Math.min((this.pagination.page * this.pagination.pageSize), this.pagination.totalItems);
         return `${start} - ${end}`;
      },
   },
   watch: {
      'pagination.pageSize'() { this.pagination.page = 1; this.processClientSideData(); },
      sortBy() { this.processClientSideData(); },
      'pagination.page'() { this.processClientSideData(); }
   },
   methods: {
      async fetchContracts() {
         this.isLoading = true;
         try {
            const response = await this.$axios.get('/sample-contracts');
            this.allTemplates = response.data.sample_contracts.map(contract => ({
               id: contract.id,
               name: contract.name,
               status: contract.is_default ? 'Default' : 'In Use',
               contractType: contract.contract_type || 'Sample Terms',
               pricingPlan: contract.pricing_type || 'N/A',
               dateCreated: moment(contract.created_at).format("DD-MMM-YYYY"),
               version: contract.version || 'v1.0',
               full_link: contract.full_link,
               date_created_for_sort: new Date(contract.created_at)
            }));
            this.processClientSideData();
         } catch (error) {
            console.error("Failed to fetch sample contracts:", error);
            this.$toast.error(error.response?.data?.message || "Could not load sample contracts.");
            this.allTemplates = [];
            this.processClientSideData();
         } finally {
            this.isLoading = false;
         }
      },

      checkDuplicateName() {
         if (!this.newSampleTerm.name) {
            this.isNewNameInvalid = false;
            return;
         }
         const isDuplicate = this.allTemplates.some(
            (template) => template.name.toLowerCase() === this.newSampleTerm.name.toLowerCase()
         );
         this.isNewNameInvalid = isDuplicate;
      },

      checkEditDuplicateName() {
         if (!this.editingTemplate || !this.editingTemplate.name) {
            this.isEditNameInvalid = false;
            return;
         }
         const isDuplicate = this.allTemplates.some(
            (template) =>
               template.id !== this.editingTemplate.id &&
               template.name.toLowerCase() === this.editingTemplate.name.toLowerCase()
         );
         this.isEditNameInvalid = isDuplicate;
      },

      async saveSampleTerm() {
         const fileInput = this.$refs.sampleTermFileRef;
         if (!fileInput || !fileInput.files || fileInput.files.length === 0) {
            this.$toast.error("Please select a file to upload.");
            return;
         }
         if (!this.newSampleTerm.name) {
             this.$toast.error("Contract Name is required.");
             return;
         }

         const file = fileInput.files[0];
         if (file.type !== 'application/pdf') {
             this.$toast.error("Only PDF files are allowed.");
             return;
         }

         this.isLoading = true;
         try {
            const encodedFile = await this.toBase64(file);
            const payload = {
               name: this.newSampleTerm.name,
               original_file_name: file.name,
               file_extension: `.${file.name.split('.').pop()}`,
               encoded_file: encodedFile.split(',')[1],
               contract_type: "Sample Terms",
               pricing_type: this.newSampleTerm.pricingType,
            };

            const response = await this.$axios.post("/sample-contracts", payload);
            if (response.data.success) {
               this.$toast.success(response.data.message);
               this.isUploadModalOpen = false;
               this.fetchContracts();
            } else {
               this.$toast.error(response.data.message);
            }
         } catch (error) {
            console.error("Failed to upload sample term:", error);
            this.$toast.error(error.response?.data?.message || "An unexpected error occurred.");
         } finally {
            this.isLoading = false;
         }
      },

      async updateSampleContract() {
         if (!this.editingTemplate) return;

         this.isLoading = true;
         const payload = {
            name: this.editingTemplate.name,
            contract_type: this.editingTemplate.contractType,
            pricing_type: this.editingTemplate.pricingPlan,
            is_default: this.editingTemplate.status === 'Default'
         };

         try {
            const fileInput = this.$refs.editTemplateFileRef;
            if (fileInput && fileInput.files && fileInput.files.length > 0) {
               const file = fileInput.files[0];
               if (file.type !== 'application/pdf') {
                  this.$toast.error("Only PDF files are allowed for the new version.");
                  this.isLoading = false;
                  return;
               }
               const encodedFile = await this.toBase64(file);
               payload.original_file_name = file.name;
               payload.file_extension = `.${file.name.split('.').pop()}`;
               payload.encoded_file = encodedFile.split(',')[1];
            }

            const response = await this.$axios.put(`/sample-contracts/${this.editingTemplate.id}`, payload);
            if (response.data.success) {
               this.$toast.success(response.data.message || "Contract updated successfully.");
               this.isEditModalOpen = false;
               this.fetchContracts();
            } else {
               this.$toast.error(response.data.message || "Failed to update contract.");
            }
         } catch (error) {
            console.error("Failed to update sample contract:", error);
            this.$toast.error(error.response?.data?.message || "An error occurred during update.");
         } finally {
            this.isLoading = false;
         }
      },

      toBase64(file) {
         return new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.readAsDataURL(file);
            reader.onload = () => resolve(reader.result);
            reader.onerror = error => reject(error);
         });
      },

      async deleteTemplate(id) {
         this.pendingDeleteId = id;
         this.confirmModalTitle = 'Delete Template';
         this.confirmModalMessage = 'Are you sure you want to delete this template?';
         this.showConfirmModal = true;
      },
      async onConfirmAction() {
         const id = this.pendingDeleteId;
         this.pendingDeleteId = null;
         if (!id) return;
         this.isLoading = true;
         try {
            const response = await this.$axios.delete(`/sample-contracts/${id}`);
            this.$toast.success(response.data.message);
            this.fetchContracts();
         } catch (error) {
            console.error(`Failed to delete template with ID: ${id}`, error);
            this.$toast.error(error.response?.data?.message || "Could not delete the template.");
         } finally {
             this.isLoading = false;
         }
      },

      processClientSideData() {
         let processedList = [...this.allTemplates];
         if (this.search) {
            const searchTerm = this.search.toLowerCase();
            processedList = processedList.filter(template =>
               template.name.toLowerCase().includes(searchTerm)
            );
         }
         processedList.sort((a, b) => {
            switch (this.sortBy) {
               case 'name_asc': return a.name.localeCompare(b.name);
               case 'date_desc': return new Date(b.date_created_for_sort) - new Date(a.date_created_for_sort);
               case 'date_asc': return new Date(a.date_created_for_sort) - new Date(b.date_created_for_sort);
               default: return 0;
            }
         });
         this.pagination.totalItems = processedList.length;
         this.pagination.totalPages = Math.ceil(processedList.length / this.pagination.pageSize) || 1;
         if (this.pagination.page > this.pagination.totalPages) {
            this.pagination.page = this.pagination.totalPages;
         }
         const start = (this.pagination.page - 1) * this.pagination.pageSize;
         const end = start + this.pagination.pageSize;
         this.templateList = processedList.slice(start, end);
      },
      onSearch() {
         this.pagination.page = 1;
         this.debounceFn();
      },
      openAddTemplateModal() {
         this.newTemplate = { name: "", type: "", notes: "" };
         this.isAddModalOpen = true;
      },
      saveNewTemplate() {
         console.log("Saving new template:", this.newTemplate);
         this.$toast.info("This feature is not yet connected to the backend.");
         this.isAddModalOpen = false;
      },
      openUploadSampleTermsModal() {
         this.newSampleTerm = { name: "", file: null, pricingType: "" };
         this.isNewNameInvalid = false;
         if (this.$refs.sampleTermFileRef) {
             this.$refs.sampleTermFileRef.value = "";
         }
         this.isUploadModalOpen = true;
      },
      openEditModal(template) {
         this.editingTemplate = { ...template };
         this.isEditNameInvalid = false;
         this.isEditModalOpen = true;
         this.$nextTick(() => {
            if (this.$refs.editTemplateFileRef) {
               this.$refs.editTemplateFileRef.value = "";
            }
         });
      },
      openPdfViewer(template) {
         if (template.full_link && template.full_link.toLowerCase().endsWith('.pdf')) {
            this.pdfViewerUrl = template.full_link;
            this.isPdfViewerOpen = true;
         } else {
            this.$toast.info("This file is not a PDF and cannot be viewed. Please download it instead.");
            window.open(template.full_link, '_blank');
         }
      },
      previousPage() { if (this.pagination.page > 1) { this.pagination.page--; } },
      nextPage() { if (this.pagination.page < this.pagination.totalPages) { this.pagination.page++; } },
   }
};
</script>

<style scoped>
/* --- General & Card Styles --- */
.filterTitle {
   font-weight: 500;
   color: #435666;
}
.filterOptions {
   font-weight: 500;
   color: #00A0C1;
}
.bordered-select {
   border: 1px solid #dee2e6 !important;
   border-radius: 8px !important;
   padding-top: 0.25rem;
   padding-bottom: 0.25rem;
   padding-left: 0.75rem;
   width: auto;
}
.contract-card {
   border: 1px solid #e0e0e0;
   box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
   border-radius: 12px;
   transition: transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out;
}
.contract-card:hover {
   transform: translateY(-5px);
   box-shadow: 0 8px 20px rgba(0, 0, 0, 0.12);
}
.contract-card .card-title {
   font-weight: 700;
   color: #333;
}
.status-in-use {
   color: #00A0C1;
   font-weight: 600;
}

/* --- Pagination Styles --- */
.pagination {
    display: inline-flex;
    align-items: center;
    border: 1px solid #dee2e6;
    border-radius: 8px;
    background-color: #fff;
    overflow: hidden;
}
.pagination-label {
    font-size: 0.9rem;
    color: #6c757d;
    font-weight: 500;
    padding: 0 8px;
    white-space: nowrap;
}
.pagination-label strong {
    color: #343a40;
}
.pagination-arrow {
    background-color: transparent;
    border: none;
    cursor: pointer;
    color: #00A0C1;
    font-size: 1rem;
    padding: 8px 12px;
    transition: background-color 0.2s ease;
}
.pagination-arrow:hover:not(:disabled) {
    background-color: #f1f3f5;
}
.pagination-arrow:disabled {
    color: #ced4da;
    cursor: not-allowed;
}


/* --- Action Button Styles --- */
.action-button-container {
   display: flex;
   align-items: center;
   justify-content: flex-start;
   gap: 6px;
}
.btn-svg-icon {
   width: 36px;
   height: 36px;
   border-radius: 50% !important;
   display: inline-flex;
   align-items: center;
   justify-content: center;
   background-color: #e6f7fa;
   color: #00A0C1;
   border: none;
   padding: 0;
   cursor: pointer;
   transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
   text-decoration: none;
   position: relative;
   overflow: hidden;
}
.btn-svg-icon:hover:not(:disabled) {
   background-color: #00A0C1;
   color: #fff;
   transform: translateY(-1px);
   box-shadow: 0 4px 12px rgba(0, 160, 193, 0.25);
}
.btn-svg-icon:active:not(:disabled) {
   transform: translateY(0);
}
.btn-svg-icon:hover:not(:disabled) .icon-svg {
   filter: brightness(0) invert(1);
}
.btn-svg-icon--delete {
   background-color: #fef2f2;
   color: #ef4444;
}
.btn-svg-icon--delete:hover:not(:disabled) {
   background-color: #ef4444;
   box-shadow: 0 4px 12px rgba(239, 68, 68, 0.25);
}
.btn-svg-icon:disabled {
   cursor: not-allowed;
   opacity: 0.5;
}
.btn-svg-icon:disabled:hover {
   background-color: #e6f7fa;
   transform: none;
   box-shadow: none;
}
.btn-svg-icon:disabled:hover .icon-svg {
   filter: none;
}
.icon-svg {
   width: 30px;
   height: 30px;
   transition: filter 0.15s ease;
}


/* --- Custom Modal Styles --- */
.modal-backdrop-custom {
   position: fixed;
   top: 0;
   left: 0;
   width: 100vw;
   height: 100vh;
   background-color: rgba(0, 0, 0, 0.5);
   display: flex;
   justify-content: center;
   align-items: center;
   z-index: 1050;
}
.modal-content-custom {
   position: relative;
   background-color: white;
   padding: 2.5rem 2rem 2rem 2rem;
   border-radius: 16px;
   width: 100%;
   max-width: 500px;
   box-shadow: 0 5px 15px rgba(0, 0, 0, 0.3);
}
.pdf-viewer-modal {
   max-width: 90vw;
   height: 90vh;
   display: flex;
   flex-direction: column;
}
.pdf-viewer-modal iframe {
   flex-grow: 1; /* Allow iframe to fill the space */
   border-radius: 8px;
}
.modal-title-custom {
   font-weight: bold;
   font-size: 1.25rem;
   text-align: left;
   margin-bottom: 0;
}
.modal-content-custom hr {
   margin-top: 1rem;
   margin-bottom: 1.5rem;
}
.close-button-custom {
   position: absolute;
   top: 1rem;
   right: 1rem;
   font-size: 2rem;
   font-weight: bold;
   line-height: 1;
   color: #888;
   background: none;
   border: none;
   cursor: pointer;
}
.close-button-custom:hover {
   color: #000;
}
.modal-form-custom .form-label {
   font-weight: 500;
   color: #00A0C1;
}
.add-button-custom {
   padding: 10px 40px;
   font-size: 1rem;
   font-weight: bold;
   border-radius: 8px;
}
.text-danger {
    color: #dc3545 !important;
}
</style>